<template>
    <div :class="modifiers" class="vc-checkout">
        <div class="vc-checkout__summary">
            <checkout-header @toggle="showItems = !showItems" class="vc-checkout__summary__header"></checkout-header>
            <base-alert class="vc-checkout__alert-no-payment-method" type="info" v-if="!storeAcceptPayment">
                This store does not have any payment methods set.
            </base-alert>
            <base-transition-expand>
                <checkout-items-list v-show="showItems"></checkout-items-list>
            </base-transition-expand>
            <checkout-coupon class="vc-checkout__summary__coupon"></checkout-coupon>
            <checkout-total></checkout-total>
            <!-- Future total component  <checkout-total class="vc-checkout__summary__total"></checkout-total>-->
        </div>

        <div class="vc-checkout__steps" v-if="storeAcceptPayment">
            <form :action="postUrl" class="=vc-checkout__form" id="checkout-form" method="POST" novalidate="novalidate"
                  ref="checkoutForm">
                <input :value="token" name="_token" type="hidden">
                <input name="storeUsername" type="hidden" v-model="storeUserName">
                <input name="custom_domain" type="hidden" v-model="isCustomDomain">
                <input name="config_id" type="hidden" v-model="configId">
                <div class="vc-checkout__steps__section">
                    <div class="vc-checkout__steps__section__header">Your Details</div>
                    <div class="vc-checkout__steps__section__main">
                        <checkout-customer></checkout-customer>
                    </div>
                </div>
                <div class="vc-checkout__steps__section">
                    <div class="vc-checkout__steps__section__header">Payment Method</div>
                    <div class="vc-checkout__steps__section__main">
                        <checkout-payment-methods></checkout-payment-methods>
                    </div>
                </div>
                <template v-if="storeAcceptStripe">
                    <base-transition-expand>
                        <div class="vc-checkout__steps__section"
                             v-show="selectedPaymentMethod === 'stripe'">
                            <div class="vc-checkout__steps__section__header">Payment</div>
                            <div class="vc-checkout__steps__section__main">
                                <checkout-payment-stripe></checkout-payment-stripe>
                            </div>
                        </div>
                    </base-transition-expand>
                    <checkout-payment-stripe-secure-auth v-if="stripeRequestSecureAuth"></checkout-payment-stripe-secure-auth>
                </template>
            </form>
            <div class="vc-checkout__steps__footer">
                <checkout-accepted-cards class="vc-checkout__steps__footer__accepted-cards"></checkout-accepted-cards>
                <checkout-continue-shopping
                        class="vc-checkout__steps__footer__continue-shopping"></checkout-continue-shopping>
            </div>
        </div>
        <recommended-beats :noTopMargin="true" :smallTitle="true" class="vc-checkout__recommended-beats"
                           v-if="customerAuthenticated"></recommended-beats>
    </div>
</template>


<script>
    import Playlist from './../../classes/playlist'
    import CheckoutHeader from '../checkout/checkout-header.vue'
    import CheckoutItemsList from '../checkout/checkout-items-list.vue'
    import CheckoutTotal from '../checkout/checkout-total.vue'
    import CheckoutCoupon from '../checkout/checkout-coupon.vue'
    import CheckoutCustomer from '../checkout/checkout-customer.vue'
    import CheckoutPaymentMethods from "../checkout/checkout-payment-methods.vue";
    import CheckoutPaymentStripe from '../checkout/checkout-payment-stripe.vue'
    import CheckoutPaymentStripeSecureAuth from '../checkout/checkout-payment-stripe-secure-auth.vue'
    import CheckoutAcceptedCards from '../checkout/checkout-accepted-cards.vue'
    import CheckoutContinueShopping from '../checkout/checkout-continue-shopping.vue'
    import RecommendedBeats from '../recommended-beats.vue'
    import {mapGetters} from 'vuex'
    import VueRecaptcha from 'vue-recaptcha';

    export default {
        name: "checkout",
        components: {
            CheckoutHeader,
            CheckoutItemsList,
            CheckoutTotal,
            CheckoutCoupon,
            CheckoutCustomer,
            CheckoutPaymentMethods,
            CheckoutPaymentStripe,
            CheckoutPaymentStripeSecureAuth,
            CheckoutAcceptedCards,
            CheckoutContinueShopping,
            RecommendedBeats,
            VueRecaptcha
        },
        beforeRouteEnter(to, from, next) {
            next(vm => {
                if (vm.$store.getters['checkout/checkoutDisabled']) {
                    vm.$router.push('/');
                    return;
                }

                if (vm.$store.getters['checkout/storeAcceptPayment'])
                    vm.$store.commit('pageFullWidthForced', true);
            })
        },
        beforeRouteLeave(to, from, next) {
            this.$store.commit('pageFullWidthForced', false);
            next();
        },
        beforeCreate() {
            this.$store.state.playlistManager.setPagePlaylist(new Playlist([], "checkout"));
        },
        mounted() {
            this.$store.state.eventTracker.checkout()
        },
        data() {
            return {
                postUrl: this.$store.state.domainUrlBuilder.build(`/checkout/pay`),
                showItems: true,
                storeUserName: ab.user.username,
                isCustomDomain: ab.isCustomDomain
            }
        },
        computed: {
            ...mapGetters('checkout', [
                'customer',
                'storeAcceptPayment',
                'storeAcceptStripe',
                'customerAuthenticated',
                'selectedPaymentMethod',
                'orderSummaryValidated',
                'formSubmit',
                'processing',
                'firstInvalidFieldAfterSubmit',
                'containerSelector',
                'formFieldsIds',
                'stripeRequestSecureAuth'
            ]),
            token() {
                return this.$store.state.csrfToken;
            },
            modifiers() {
                return {
                    'vc-checkout--no-payment-method': !this.storeAcceptPayment
                }
            },
            configId() {
                return this.$store.state.currentConfig.id;
            }
        },
        watch: {
            formSubmit(submit) {
                if (submit)
                // Do not remove nexttick, otherwise the request will fail, added cause of vuex store latency
                    this.$nextTick(() => this.$refs.checkoutForm.submit());
            },
            orderSummaryValidated(validated) {
                if (!validated && !this.processing) {
                    this.$router.push('/');
                }
            },
            firstInvalidFieldAfterSubmit(fieldId) {
                if (fieldId) {
                    this.$nextTick(() => {
                        this.$scrollTo(`#${fieldId}`, 1000, {
                            container: this.containerSelector,
                            offset: -135,
                            easing: 'ease',
                            cancelable: false
                        });
                    });
                    this.$nextTick(() => {
                        this.$store.commit('checkout/firstInvalidFieldAfterSubmit', null);
                    });
                }
            },
            selectedPaymentMethod(method) {
                if(method === 'stripe')
                    this.$nextTick(() => {
                        this.$scrollTo(`#${this.formFieldsIds.stripeCardholderName}`, 1000, {
                            container: this.containerSelector,
                            offset: -200,
                            easing: 'ease',
                            cancelable: false
                        });
                    });
            }
        }
    }
</script>

<style lang="scss">
    .vc-checkout {
        .beat-container {
            max-width: 250px;
        }
    }
</style>

<style lang="scss" scoped>
    $gap-x: 1.5rem;
    $gap-y: 1.125rem;
    $padding: $gap-x $gap-y;

    .vc-checkout {
        display: grid;
        grid-template-columns: 1.2fr 1fr;
        grid-template-rows: auto 1fr;
        grid-template-areas: "summary steps" "recommended steps";
        height: 100%;
        user-select: none;

        &__alert-no-payment-method {
            margin-bottom: 1rem;
        }

        &__summary {
            padding: $padding;
            grid-area: summary;

            &__header {
                margin-bottom: 1rem;
            }

            &__coupon {
                margin-top: 15px;
            }

            &__total {
                color: var(--primary-text-color-less-emphasized);
                margin-top: 12px;
            }
        }

        &__steps {
            color: var(--primary-text-color-less-emphasized);
            padding: $gap-x 4vw;
            background: var(--background-emphasized);
            grid-area: steps;
            border-left: 1px solid var(--primary-text-color-faded-more);

            &__section {
                margin-bottom: 20px;

                &__header {
                    font-size: 18px;
                    font-weight: 600;
                    color: var(--primary-text-color-emphasized);
                    margin-bottom: 17px;
                }
            }

            &__footer {
                &__continue-shopping {
                    margin: 20px auto 20px auto;
                }

                &__error {
                    margin: 12px 0;
                }
            }
        }

        &__recommended-beats {
            grid-area: recommended;
            padding: $gap-x 0;
            margin: 0 $gap-y;
            border-top: 1px solid var(--primary-text-color-faded-more);
        }

        &--no-payment-method {
            grid-template-columns: auto;
            grid-template-areas: unset;
            height: auto;
        }
    }

    @media screen and (max-width: 870px) {
        .vc-checkout {
            grid-template-columns: auto;
            grid-template-rows: auto auto auto;
            grid-template-areas: "summary" "steps" "recommended";

            &__steps {
                padding: $padding;
                border-left: none;
                border-top: 1px solid var(--primary-text-color-faded-more);
                border-bottom: 1px solid var(--primary-text-color-faded-more);
            }

            &__recommended-beats {
                border-top: none;
            }

            &--no-payment-method {
                .vc-checkout__recommended-beats {
                    border-top: 1px solid var(--primary-text-color-faded-more);
                }
            }
        }
    }
</style>